import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { cn } from '@/utils/cn';
import { BUILDER_STEPS, type BuilderStep } from '../builder/draft';

interface StepIndicatorProps {
  current: BuilderStep;
  /** Whether the user may open a step now. */
  canOpen: (step: BuilderStep) => boolean;
  isDone: (step: BuilderStep) => boolean;
  onOpen: (step: BuilderStep) => void;
}

/**
 * The five steps, as buttons a keyboard reaches in order.
 *
 * The current step is marked for assistive technology as well as by colour, and a finished one carries a tick and the word for it. On a phone the labels give way to a "Step 2 of 5" line so nothing scrolls sideways.
 */
export function StepIndicator({ current, canOpen, isDone, onOpen }: StepIndicatorProps) {
  const { t } = useTranslation();
  const index = BUILDER_STEPS.indexOf(current);

  return (
    <nav aria-label={t('opticalOrders.steps.label')} className="space-y-2">
      <p className="text-muted text-sm sm:hidden">
        {t('opticalOrders.steps.progress', {
          current: index + 1,
          total: BUILDER_STEPS.length,
          name: t(`opticalOrders.steps.${current}`),
        })}
      </p>
      <ol className="flex gap-1.5 sm:gap-2">
        {BUILDER_STEPS.map((step, position) => {
          const isCurrent = step === current;
          const done = isDone(step);
          return (
            <li key={step} className="min-w-0 flex-1">
              <button
                type="button"
                onClick={() => onOpen(step)}
                disabled={!isCurrent && !canOpen(step)}
                aria-current={isCurrent ? 'step' : undefined}
                className={cn(
                  'flex w-full min-w-0 flex-col items-start gap-1 rounded-lg border-t-4 px-1 pt-2 text-start text-sm',
                  'focus-visible:ring-brand-200 focus-visible:ring-2 focus-visible:outline-none',
                  'disabled:cursor-not-allowed disabled:opacity-60',
                  isCurrent
                    ? 'border-brand-600 text-ink'
                    : done
                      ? 'border-emerald-500 text-ink'
                      : 'border-line text-muted',
                )}
              >
                <span className="flex items-center gap-1 font-medium">
                  <span dir="ltr" className="tabular-nums">
                    {position + 1}
                  </span>
                  {done ? (
                    <>
                      <Check className="size-3.5 text-emerald-600" aria-hidden />
                      <span className="sr-only">{t('opticalOrders.steps.complete')}</span>
                    </>
                  ) : null}
                </span>
                <span className="hidden truncate sm:block">{t(`opticalOrders.steps.${step}`)}</span>
                <span className="sr-only sm:hidden">{t(`opticalOrders.steps.${step}`)}</span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
